import { useState, useMemo, useCallback, useEffect } from "react";
import {
  MagnifyingGlassIcon,
  XMarkIcon,
  ArrowTopRightOnSquareIcon,
  ChevronRightIcon,
  ChevronDownIcon,
} from "@heroicons/react/24/outline";
import clsx from "clsx";
import { FilterMenuConfig } from "@/shared/models/filterTypes";
import { useStateWithOptionalPersistence } from "@/hooks/useStateWithOptionalPersistence";
import AccordionSection from "./AccordionSection";
import WatchlistTermsModal from "./WatchlistTermsModal";
import MatrixSection from "./MatrixSection";
import type { MatrixGroup } from "@/shared/hooks/useFilterHighlightMatrix";
import FilterMenu from "../filter-menu";
import TagLibraryResults, { TAG_LIBRARY_HINT } from "../TagLibraryResults";
import { useTagLibrarySearch } from "@/shared/hooks";

export interface FilterPanelProps {
  /** Array of filter menu configurations */
  filterMenus: FilterMenuConfig[];
  /** Current filter selections by menu ID */
  selections: Record<string, string[]>;
  /** Callback when selections change */
  onChange: (selections: Record<string, string[]>) => void;
  /** Whether the panel is open/visible */
  isOpen: boolean;
  /** Optional key for persisting expansion state */
  persistKey?: string;
  /** @deprecated Use onClearSelections instead */
  footer?: React.ReactNode;
  /** Callback to close the panel */
  onClose?: () => void;
  /** Callback to clear all selections */
  onClearSelections?: () => void;
  /** Panel variant for visual distinction */
  variant?: "filter" | "highlight";
  /** Merged list tables, rendered above the remaining sections. */
  /** How many selections THIS panel's verb has active — drives the clear
   *  button. Passed in rather than counted from `selections`, which is a
   *  merged map and would otherwise report the other panel's state. */
  activeCount?: number;
  matrixGroups?: MatrixGroup[];
  onMatrixToggle?: (sectionId: string, optionId: string, isDisabled: boolean) => void;
  /**
   * Opt in to searching the whole tag library from this panel's search box.
   *
   * `menuId` names the section whose selections are raw tag ids, since that is
   * where a tag picked from the library has to land. Hosts without such a
   * section leave this unset and the panel behaves exactly as before.
   */
  tagLibrary?: { menuId: string };
}

export default function FilterPanel({
  filterMenus,
  selections,
  onChange,
  isOpen,
  persistKey,
  footer,
  onClose,
  onClearSelections,
  variant,
  activeCount = 0,
  matrixGroups = [],
  onMatrixToggle,
  tagLibrary,
}: FilterPanelProps) {
  const [searchQuery, setSearchQuery] = useState("");

  // Persist which sections are expanded
  const [expandedSections, setExpandedSections] =
    useStateWithOptionalPersistence<string[]>(
      persistKey ? `${persistKey}-expanded` : undefined,
      []
    );

  // Calculate selection counts per menu
  const selectionCounts = useMemo(() => {
    return filterMenus.reduce(
      (acc, menu) => {
        acc[menu.id] = selections[menu.id]?.length || 0;
        return acc;
      },
      {} as Record<string, number>
    );
  }, [filterMenus, selections]);

  /**
   * What is currently on, gathered from BOTH sources.
   *
   * Watchlists moved into `matrixGroups`, but the Active summary was only ever
   * taught about `filterMenus` — so the footer counted a live selection while
   * the summary above it showed nothing, and the one row that told you what
   * was actually applied quietly stopped appearing.
   */
  const matrixActive = useMemo(() => {
    const column = variant === "highlight" ? "highlight" : "filter";
    return matrixGroups
      .map((group) => {
        const items = group.rows
          .filter((row) => {
            const sectionId =
              column === "highlight" ? row.highlightSectionId : row.filterSectionId;
            return (
              !!sectionId && (selections[sectionId] ?? []).includes(String(row.id))
            );
          })
          .map((row) => ({
            id: String(row.id),
            label: row.label,
            // Matrix rows route through their own section, not the group id —
            // without this the chip's × writes to a key nothing reads.
            sectionId:
              (column === "highlight"
                ? row.highlightSectionId
                : row.filterSectionId) ?? undefined,
          }));
        return { menuId: group.id, menuLabel: group.label, items };
      })
      .filter((group) => group.items.length > 0);
  }, [matrixGroups, selections, variant]);

  // Total selections for header badge
  const totalSelections = useMemo(() => {
    const fromMenus = Object.values(selectionCounts).reduce(
      (sum, count) => sum + count,
      0
    );
    const fromMatrix = matrixActive.reduce(
      (sum, group) => sum + group.items.length,
      0
    );
    return fromMenus + fromMatrix;
  }, [selectionCounts, matrixActive]);

  // Build flat list of all active selections with labels, grouped by menu
  const activeItems = useMemo(() => {
    const groups: {
      menuId: string;
      menuLabel: string;
      items: { id: string; label: string; sectionId?: string }[];
    }[] = [];

    for (const menu of filterMenus) {
      const selectedIds = selections[menu.id] || [];
      if (selectedIds.length === 0) continue;

      const items: { id: string; label: string; sectionId?: string }[] = [];
      for (const id of selectedIds) {
        const option = menu.filterOptions.find(
          (o) => String(o.id) === String(id)
        );
        if (option && !option.isHeader) {
          items.push({ id: String(option.id), label: option.label });
        }
      }
      if (items.length > 0) {
        // Clean up menu label
        let menuLabel = menu.label;
        if (menuLabel.startsWith("Filter by "))
          menuLabel = menuLabel.replace("Filter by ", "");
        if (menuLabel.startsWith("Highlight by "))
          menuLabel = menuLabel.replace("Highlight by ", "");
        groups.push({ menuId: menu.id, menuLabel, items });
      }
    }
    return [...matrixActive, ...groups];
  }, [filterMenus, selections, matrixActive]);

  /**
   * Open when something is already applied.
   *
   * Collapsed-by-default is right for a panel you came to browse, and wrong
   * for one that arrives with selections already made. After the setup builder
   * applies a plan, the panel said "Clear active (1)" and showed a count
   * against a section header — so the user could see that something was
   * filtering or highlighting their wire, but not what, and the one control
   * offered was to clear it. Naming the selection is the difference between a
   * dashboard that was built for you and one that has something unexplained
   * switched on.
   *
   * Initial state only: collapsing it stays collapsed, and it does not
   * re-open on later selection changes.
   */
  const [activeExpanded, setActiveExpanded] = useState(
    () => activeItems.length > 0
  );
  /**
   * Which row's contents to show. Carries the section as well as the id
   * because ids are only unique within a store — see WatchlistTermsModal.
   */
  const [inspecting, setInspecting] = useState<
    { id: string; sectionId: string } | null
  >(null);

  // Everything starts collapsed: the panel opens as a short list of section
  // headers you choose from, rather than a wall of checkboxes.
  const isExpanded = useCallback(
    (menu: FilterMenuConfig) => expandedSections.includes(menu.id),
    [expandedSections]
  );

  // Multi-expand — several groups can be open together. Single-expand made
  // sense when opening one section auto-collapsed a competing one.
  const toggleSection = useCallback(
    (menu: FilterMenuConfig) => {
      setExpandedSections((prev) =>
        prev.includes(menu.id)
          ? prev.filter((id) => id !== menu.id)
          : [...prev, menu.id]
      );
    },
    [setExpandedSections]
  );

  // Mutual exclusivity is expressed by the section builder now (disabled
  // options + FilterConflictNote), not by disabling whole sections here —
  // the old version blanked the Highlight column too, which is orthogonal
  // and must always stay available.
  const processedMenus = filterMenus;

  // Filter menus based on search query
  const filteredMenus = useMemo(() => {
    if (!searchQuery.trim()) {
      return processedMenus;
    }

    const query = searchQuery.toLowerCase();
    return processedMenus
      .map((menu) => {
        // Filter options by search query
        const filteredOptions = menu.filterOptions.filter((option) =>
          option.label.toLowerCase().includes(query)
        );

        // Also filter subsections if they exist
        const filteredSubsections = menu.subsections
          ?.map((sub) => ({
            ...sub,
            filterIds: sub.filterIds.filter((id) => {
              const option = menu.filterOptions.find((opt) => opt.id === id);
              return option?.label.toLowerCase().includes(query);
            }),
          }))
          .filter((sub) => sub.filterIds.length > 0);

        return {
          ...menu,
          filterOptions: filteredOptions,
          subsections: filteredSubsections?.length
            ? filteredSubsections
            : undefined,
        };
      })
      .filter((menu) => menu.filterOptions.length > 0);
  }, [processedMenus, searchQuery]);

  // ── Tag library search ────────────────────────────────────────────────
  // Opt-in per host. The hook no-ops below its minimum query length, so this
  // costs nothing until someone actually types.
  const tagLibrary_ = useTagLibrarySearch(tagLibrary ? searchQuery : "");

  /**
   * Tags the panel already lists under its own sections. The curated menu tags
   * are options in the same section these results write to, so without this a
   * search for "energy" shows the same tag twice, once ticked and once not.
   */
  const listedTagIds = useMemo(() => {
    if (!tagLibrary) return new Set<string>();
    const menu = filterMenus.find((m) => m.id === tagLibrary.menuId);
    return new Set((menu?.filterOptions ?? []).map((o) => String(o.id)));
  }, [filterMenus, tagLibrary]);

  const handleTagLibraryToggle = useCallback(
    (tagId: string) => {
      if (!tagLibrary) return;
      const current = selections[tagLibrary.menuId] || [];
      onChange({
        ...selections,
        [tagLibrary.menuId]: current.includes(tagId)
          ? current.filter((id) => id !== tagId)
          : [...current, tagId],
      });
    },
    [tagLibrary, selections, onChange]
  );

  // Handle toggle with disabled check
  const handleToggle = useCallback(
    (menuId: string, filterId: string, isDisabled: boolean) => {
      if (isDisabled) return;

      const currentSelections = selections[menuId] || [];
      const isSelected = currentSelections.includes(filterId);

      const updatedSelections = isSelected
        ? currentSelections.filter((id) => id !== filterId)
        : [...currentSelections, filterId];

      onChange({
        ...selections,
        [menuId]: updatedSelections,
      });
    },
    [selections, onChange]
  );

  // Clear search when panel closes
  useEffect(() => {
    if (!isOpen) {
      setSearchQuery("");
    }
  }, [isOpen]);

  // Auto-expand sections with matching search results
  useEffect(() => {
    if (searchQuery.trim() && filteredMenus.length > 0) {
      const menusWithResults = filteredMenus.map((m) => m.id);
      setExpandedSections((prev) => {
        const newExpanded = [...new Set([...prev, ...menusWithResults])];
        return newExpanded;
      });
    }
  }, [searchQuery, filteredMenus, setExpandedSections]);

  if (!isOpen) return null;

  return (
    <div
      className={clsx(
        "bg-gray-800 shadow-lg shadow-black/30",
        variant === "highlight" && "border-x border-b border-gray-700/50",
        variant === "filter" && "border-x border-b border-squawk-blue/50",
        !variant && "border-b border-gray-700/50"
      )}
    >
      {/* Header — just the search input. Bulk-clear moved into the Active summary; panel close is the Done button. */}
      <div className="px-3 py-2 border-b border-gray-700/30">
        <div className="relative">
          <MagnifyingGlassIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 size-3.5 text-gray-500" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search watchlists, filters..."
            className="w-full pl-8 pr-8 py-1.5 text-xs bg-gray-900 text-gray-200 border border-gray-700 rounded focus:outline-none focus:border-squawk-blue placeholder:text-gray-500"
            autoFocus
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 text-gray-500 hover:text-gray-300 transition-colors"
              aria-label="Clear search text"
            >
              <XMarkIcon className="size-3" />
            </button>
          )}
        </div>
        {tagLibrary && (
          <p className="mt-1.5 text-2xs text-gray-500">{TAG_LIBRARY_HINT}</p>
        )}
      </div>

      {/* Active selections summary (also hosts the bulk-clear button on the right) */}
      {totalSelections > 0 && (
        <div className="border-b border-gray-700/30">
          <div className="flex items-center">
            <button
              onClick={() => setActiveExpanded(!activeExpanded)}
              className={clsx(
                "flex-1 px-3 py-2 flex items-center gap-2 text-left text-xs transition-colors focus:outline-none",
                activeExpanded
                  ? variant === "highlight"
                    ? "bg-terminal-orange/5 text-terminal-orange"
                    : "bg-squawk-blue/5 text-squawk-blue"
                  : "text-gray-400 hover:text-gray-200"
              )}
            >
              {activeExpanded ? (
                <ChevronDownIcon className="size-3 flex-shrink-0" />
              ) : (
                <ChevronRightIcon className="size-3 flex-shrink-0" />
              )}
              <span className="font-medium flex-1">Active</span>
              <span
                className={clsx(
                  "px-1.5 py-0.5 text-2xs font-medium rounded",
                  variant === "highlight"
                    ? "bg-terminal-orange/20 text-terminal-orange"
                    : "bg-squawk-blue/20 text-squawk-blue"
                )}
              >
                {totalSelections}
              </span>
            </button>
            {/* The header's red Clear is gone. Red reads as destructive, and
                this only unticks checkboxes — it was the loudest thing in the
                panel for its most ordinary action. "Clear active (N)" at the
                foot does the same job, names what it clears, and sits with the
                other actions instead of duplicating one of them up here. */}
          </div>
          {activeExpanded && (
            <div className="px-3 pb-2 space-y-2">
              {activeItems.map((group) => (
                <div key={group.menuId}>
                  <div className="text-2xs text-gray-500 uppercase tracking-wider mb-1">
                    {group.menuLabel}
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {group.items.map((item) => (
                      <span
                        key={item.id}
                        className={clsx(
                          "inline-flex items-center gap-1 pl-2 pr-1 py-0.5 rounded text-xs border",
                          variant === "highlight"
                            ? "bg-terminal-orange/10 border-terminal-orange/30 text-terminal-orange"
                            : "bg-squawk-blue/10 border-squawk-blue/30 text-squawk-blue"
                        )}
                      >
                        {/* Same gesture as the list below: the name opens
                            what is in it, the × takes it off. Only matrix rows
                            are watchlists — an asset class or source has no
                            contents to show, so those stay plain text. */}
                        {item.sectionId ? (
                          <button
                            type="button"
                            onClick={() => setInspecting({ id: item.id, sectionId: item.sectionId! })}
                            title={`${item.label} — see what's in it`}
                            className="cursor-pointer underline-offset-2 hover:underline"
                          >
                            {item.label}
                          </button>
                        ) : (
                          item.label
                        )}
                        <button
                          onClick={() =>
                            item.sectionId
                              ? onMatrixToggle?.(item.sectionId, item.id, false)
                              : handleToggle(group.menuId, item.id, false)
                          }
                          aria-label={`Remove ${item.label}`}
                          className="p-0.5 hover:text-gray-200 transition-colors"
                        >
                          <XMarkIcon className="size-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Accordion sections */}
      <div className="divide-y divide-gray-700/30 max-h-96 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-700 scrollbar-track-transparent">
        {matrixGroups.map((group) => (
          <AccordionSection
            key={group.id}
            label={group.label}
            activeCount={
              variant === "highlight"
                ? group.highlightCount
                : group.filterCount
            }
            isExpanded={expandedSections.includes(group.id)}
            onToggle={() =>
              setExpandedSections((prev) =>
                prev.includes(group.id)
                  ? prev.filter((id) => id !== group.id)
                  : [...prev, group.id]
              )
            }
          >
            <MatrixSection
              rows={group.rows}
              selections={selections}
              onToggle={onMatrixToggle ?? (() => {})}
              onInspect={(id, sectionId) => setInspecting({ id, sectionId })}
              column={variant === "highlight" ? "highlight" : "filter"}
              conflictNote={group.conflictNote}
              searchQuery={searchQuery.trim() ? searchQuery : undefined}
            />
          </AccordionSection>
        ))}

        {tagLibrary && searchQuery.trim() && (
          <TagLibraryResults
            tags={tagLibrary_.tags}
            isLoading={tagLibrary_.isLoading}
            isEmpty={tagLibrary_.isEmpty}
            query={searchQuery.trim()}
            selectedIds={selections[tagLibrary.menuId] || []}
            onToggle={handleTagLibraryToggle}
            hiddenIds={listedTagIds}
          />
        )}

        {/* The library results are a result too — without them in this test a
            search that only the library answered rendered "No filters match"
            directly above the matches. */}
        {filteredMenus.length === 0 &&
        matrixGroups.length === 0 &&
        !(tagLibrary && searchQuery.trim()) ? (
          <div className="px-4 py-6 text-center text-xs text-gray-500">
            No filters match &quot;{searchQuery}&quot;
          </div>
        ) : (
          filteredMenus.map((menu) => (
            <AccordionSection
              key={menu.id}
              label={menu.label}
              activeCount={selectionCounts[menu.id] || 0}
              isExpanded={isExpanded(menu)}
              onToggle={() => toggleSection(menu)}
              disabled={menu.disabled}
              helperText={menu.helperText}
            >
              <FilterMenu
                label={menu.label}
                description={menu.description}
                filters={menu.filterOptions}
                selected={selections[menu.id] || []}
                onToggle={(filterId) =>
                  handleToggle(
                    menu.id,
                    String(filterId),
                    menu.disabled ?? false
                  )
                }
                disabled={menu.disabled}
                subsections={menu.subsections}
                footer={menu.footer}
                emptyLabel="No options available"
                className="!static !min-w-0 !max-w-none !w-full !rounded-none !p-0 !space-y-1.5"
                showCount={false}
                collapsible={false}
                hideHeader
                searchQuery={searchQuery.trim() ? searchQuery : undefined}
              />
            </AccordionSection>
          ))
        )}
      </div>

      {/* Footer: Manage in Library + Done button. Sticky to bottom so Done is always reachable on a short widget. */}
      <div className="sticky bottom-0 z-10 bg-gray-800 px-3 py-2 border-t border-gray-700/30 flex items-center justify-between gap-2">
        <a
          href="/watchlists-filters"
          className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-squawk-blue transition-colors"
        >
          <ArrowTopRightOnSquareIcon className="size-3.5" />
          Manage in Library
        </a>
        <div className="flex items-center gap-2">
          {/* Only shown when something is on, so the footer stays quiet at
              rest and the escape is obvious the moment it's needed. */}
          {activeCount > 0 && onClearSelections && (
            <button
              onClick={onClearSelections}
              className="px-2 py-1 text-xs font-medium rounded border border-gray-600 bg-transparent text-gray-300 hover:border-gray-500 hover:text-white transition-colors"
            >
              Clear active ({activeCount})
            </button>
          )}
          {onClose && (
          <button
            onClick={onClose}
            className={clsx(
              "px-3 py-1 text-xs font-medium rounded text-white transition-colors",
              variant === "highlight"
                ? "bg-terminal-orange hover:bg-terminal-orange/80"
                : "bg-squawk-blue hover:bg-squawk-blue/80"
            )}
          >
            Done
          </button>
          )}
        </div>
      </div>

      <WatchlistTermsModal
        id={inspecting?.id ?? null}
        sectionId={inspecting?.sectionId}
        onClose={() => setInspecting(null)}
      />
    </div>
  );
}
